/**
 * Common AI Utilities
 *
 * Shared logic for ALL AI players:
 * - Rule enforcement (Virus lock, zombification, entanglement, etc.)
 * - Legal move generation with game rules
 * - Lightweight simulation engine
 * - Time management
 *
 * This ensures consistency across Priority, Minimax, MCTS, and Zenith AIs
 */

import { GameState, IMove, ISquare, Position, MoveType, PieceType, State } from "../../types";
import { directions, animalPieceTypes } from "../../const";
import { cloneFullGameState } from "../../MCTS_v2/utils/MCTSUtils";
import { ZodiacFEN } from "../../Fen/ZodiacFEN";
import { UUID } from "crypto";
import { Piece } from "../../Piece";

export interface MoveInfo {
  fromPosition: Position;
  move: IMove;
}

/**
 * Get all legal moves for the current position
 * ENFORCES GAME RULES:
 * - Virus lock: When Virus is infecting, only Virus can move
 * - Zombification: Allows moving zombified enemy pieces
 */
/**
 * Check if there are enemy animal pieces within Virus movement range from a position.
 * Virus moves 2 squares in 8 directions (or 1 square at board edge).
 * Used to allow friendly infections only when they serve as stepping stones to enemies.
 */
function hasEnemyVirusTargetFromPosition(
  position: Position,
  board: ISquare[][],
  virusIsLight: boolean
): boolean {
  for (const direction of directions) {
    const twoAway = {
      row: position.row + direction[1] * 2,
      col: position.col + direction[0] * 2,
    };
    if (twoAway.row >= 0 && twoAway.row < board.length && twoAway.col >= 0 && twoAway.col < board[0].length) {
      if (board[twoAway.row][twoAway.col].Pieces.some(p => animalPieceTypes.includes(p.type) && p.isLight !== virusIsLight)) {
        return true;
      }
    } else {
      const oneAway = {
        row: position.row + direction[1],
        col: position.col + direction[0],
      };
      if (oneAway.row >= 0 && oneAway.row < board.length && oneAway.col >= 0 && oneAway.col < board[0].length) {
        if (board[oneAway.row][oneAway.col].Pieces.some(p => animalPieceTypes.includes(p.type) && p.isLight !== virusIsLight)) {
          return true;
        }
      }
    }
  }
  return false;
}

export function getLegalMoves(gameState: GameState): MoveInfo[] {
  const moves: MoveInfo[] = [];

  // CRITICAL: Check if a piece has a bonus turn lock (Virus infecting, Monkey swing, etc.)
  // If a piece is locked, ONLY that piece can move
  const bonusTurnLockPosition = findBonusTurnLock(gameState);

  if (bonusTurnLockPosition) {
    const lockSquare = gameState.board[bonusTurnLockPosition.row][bonusTurnLockPosition.col];
  }

  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 5; col++) {
      const pieces = gameState.board[row][col].Pieces;
      const position: Position = { row, col };

      for (const piece of pieces) {
        // Allow control if piece is the current player's color, or if it is zombified
        const isOwnColor = piece.isLight === gameState.isLightTurn;
        const isZombified = piece.zombifiedStatus?.isZombified || false;
        if (!isOwnColor && !isZombified) continue;

        // Poisoned pieces cannot contribute root moves or deeper search lines.
        if (piece.poisonedStatus?.isPoisoned) continue;

        // ENTANGLE BLOCK: Entangled pieces CANNOT move (critical bug fix)
        if (piece.isEntangled) {
          continue; // Skip all entangled pieces - they are immobilized!
        }

        // BONUS TURN LOCK: If a piece has a bonus turn, ONLY allow that piece to move
        if (bonusTurnLockPosition) {
          // Skip all pieces except the one at the locked position
          if (row !== bonusTurnLockPosition.row || col !== bonusTurnLockPosition.col) {
            continue; // Skip pieces at different positions
          }
          // Additionally verify this piece matches the locked piece ID
          if (gameState.activeBonusTurnPieceId && piece.id !== gameState.activeBonusTurnPieceId) {
            continue; // Skip if this isn't the exact locked piece
          }
        }

        try {
          // Check if piece has the method (safety check for cloned pieces)
          if (typeof piece.getValidMovesForPiece !== 'function') {
            continue;
          }

          let validMoves = piece.getValidMovesForPiece(
            gameState.board,
            position,
            gameState.isLightTurn,
            undefined
          );

          // Smart Virus friendly-fire prevention:
          // - NEVER infect own king (suicidal - virus captures king on departure)
          // - Allow friendly infection ONLY if enemies are reachable from destination (stepping stone)
          // - Always allow enemy infections
          if (piece.type === PieceType.Virus) {
            validMoves = validMoves.filter(m => {
              if (m.moveType !== MoveType.infect || !m.toPiece) return true;
              if (m.toPiece.isLight !== piece.isLight) return true; // enemy target - always OK
              if (m.toPiece.isKing) return false; // NEVER infect own king
              return hasEnemyVirusTargetFromPosition(m.position, gameState.board, piece.isLight);
            });
          }

          // Bonus turn move restriction for Penguin (mirrors MovementManager filter)
          // AI path doesn't pass gameStateManager, so PenguinMovement can't detect bonus state internally
          if (gameState.activeBonusTurnPieceId === piece.id && piece.type === PieceType.Penguin) {
            const isZombified = piece.zombifiedStatus?.isZombified || false;
            if (isZombified) {
              // Zombie bonus: slide/capture/endTurn only (no rotation)
              validMoves = validMoves.filter(m =>
                m.moveType === MoveType.regular ||
                m.moveType === MoveType.capture ||
                m.moveType === MoveType.endTurn
              );
            } else {
              // Normal bonus: rotate/endTurn only (no slide/capture)
              validMoves = validMoves.filter(m =>
                m.moveType === MoveType.rotate ||
                m.moveType === MoveType.endTurn
              );
            }
          }

          // Monkey bonus turn: prune the immediate reverse swing.
          // Mirrors MonkeyMovement.isBlockedReturnSwing, which can't fire here
          // because getValidMovesForPiece is called with gameStateManager=undefined.
          // Without this, the AI burns search depth oscillating between the swing's
          // origin and destination via chained bonus turns.
          if (
            gameState.activeBonusTurnPieceId === piece.id &&
            piece.type === PieceType.Monkey &&
            gameState.lastSwingMonkeyId === piece.id &&
            gameState.lastSwingFromPosition &&
            gameState.lastSwingToPosition &&
            position.row === gameState.lastSwingToPosition.row &&
            position.col === gameState.lastSwingToPosition.col
          ) {
            const blockTo = gameState.lastSwingFromPosition;
            validMoves = validMoves.filter(m =>
              !(m.position.row === blockTo.row && m.position.col === blockTo.col)
            );
          }

          // Virus bonus turn: ALWAYS include endTurn option
          // VirusMovement can't detect bonus state without gameStateManager,
          // so we inject endTurn here to give AI the choice to stop infecting
          if (gameState.activeBonusTurnPieceId === piece.id && piece.type === PieceType.Virus) {
            const hasEndTurn = validMoves.some(m => m.moveType === MoveType.endTurn);
            if (!hasEndTurn) {
              validMoves.push({
                id: `${piece.id}-end-turn` as any,
                pieceId: piece.id,
                position: position,
                toPiece: null,
                moveType: MoveType.endTurn,
                sideEffects: [],
              });
            }
          }

          for (const move of validMoves) {
            moves.push({ fromPosition: position, move });
          }
        } catch (error) {
        }
      }
    }
  }

  // CRITICAL FIX: If Virus bonus turn has NO moves, synthesize forced end turn move
  // When Virus exhausts all infection targets, VirusMovement returns [] (empty array)
  // This signals "auto-end turn" for humans, but AI needs an explicit move to execute
  if (moves.length === 0 && bonusTurnLockPosition && gameState.activeBonusTurnPieceId) {

    // Find the locked Virus piece
    const lockSquare = gameState.board[bonusTurnLockPosition.row][bonusTurnLockPosition.col];
    const lockedPiece = lockSquare.Pieces.find(p => p.id === gameState.activeBonusTurnPieceId);

    if (lockedPiece && lockedPiece.type === PieceType.Virus) {
      // Create forced end turn move for AI
      const endTurnMove: IMove = {
        id: crypto.randomUUID() as UUID,
        position: bonusTurnLockPosition, // Stay in same position
        toPiece: null,
        moveType: MoveType.endTurn,
        sideEffects: [],
        pieceId: lockedPiece.id
      };

      moves.push({
        fromPosition: bonusTurnLockPosition,
        move: endTurnMove
      });

    }
  }

  if (bonusTurnLockPosition) {
  }

  return moves;
}

/**
 * Find if a piece is locked during a bonus turn (Virus infecting, Monkey swing, etc.)
 * Returns the position of the locked piece, or null
 *
 * CHECKS TWO CASES:
 * 1. activeBonusTurnPieceId (general mechanism for ANY piece with bonus turn)
 * 2. Virus stacking (legacy check for Virus-specific infection lock)
 */
export function findBonusTurnLock(gameState: GameState): Position | null {
  // CASE 1: Check activeBonusTurnPieceId (works for Monkey swing, Virus, Penguin, etc.)
  if (gameState.activeBonusTurnPieceId) {

    // Find the piece with this ID on the board
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;

        for (const piece of pieces) {
          if (piece.id === gameState.activeBonusTurnPieceId) {
            return { row, col };
          }
        }
      }
    }

  }

  // Legacy Virus lock (CASE 2) removed: On regular turns, any piece can move
  // even when a Virus is stacked/infecting. The lock only applies during bonus turns
  // via activeBonusTurnPieceId (CASE 1 above).

  return null;
}

/**
 * @deprecated Use findBonusTurnLock instead
 * This alias exists for backward compatibility
 */
export function findVirusLock(gameState: GameState): Position | null {
  return findBonusTurnLock(gameState);
}

/**
 * Simulate a move and return the new game state
 *
 * HANDLES ALL ZODIAC CHESS MECHANICS:
 * - Multi-piece stacking (Virus, House, etc.)
 * - Virus capture-on-exit (captures piece it leaves behind, even allies!)
 * - Lane swaps, Lily jumps, captures
 * - Piece ability states (lily, shed, charged, etc.)
 */
export function simulateMove(
  gameState: GameState,
  moveInfo: MoveInfo
): GameState | null {
  try {
    // Create a deep copy of the game state
    const newGameState = cloneFullGameState(gameState);

    // Get squares
    const fromSquare = newGameState.board[moveInfo.fromPosition.row][moveInfo.fromPosition.col];
    const toSquare = newGameState.board[moveInfo.move.position.row][moveInfo.move.position.col];

    if (fromSquare.Pieces.length === 0) {
      return null;
    }

    // Find the actual piece that's moving
    let movingPiece = fromSquare.Pieces.find(p => p.id === moveInfo.move.pieceId);
    if (!movingPiece && fromSquare.Pieces.length === 1) {
      movingPiece = fromSquare.Pieces[0];
    }
    if (!movingPiece) {
      // If multiple pieces and can't find by ID, assume the non-Virus piece is moving
      movingPiece = fromSquare.Pieces.find(p => p.type !== PieceType.Virus) || fromSquare.Pieces[0];
    }

    // HANDLE DIFFERENT MOVE TYPES
    if (moveInfo.move.moveType === MoveType.endTurn) {
      // ====== END TURN (Virus choosing to end bonus turn chain) ======
      // RULE 3.14f: Virus captures host when it leaves
      // "End turn" = Virus leaves the host and captures it, ending the bonus turn chain
      // The Virus stays on the square, but the host piece below is captured
      const piecesOnSquare = fromSquare.Pieces.slice();
      const virusIndex = piecesOnSquare.findIndex(p => p === movingPiece);

      if (virusIndex !== -1 && piecesOnSquare.length > 1) {
        // Virus is stacked with another piece - capture all pieces except Virus
        const capturedPieces = piecesOnSquare.filter(p => p !== movingPiece);
        const virusPiece = movingPiece;
        if (virusPiece) {
          const friendlyCasualties = capturedPieces.filter(p => p.isLight === virusPiece.isLight);
          if (friendlyCasualties.length > 0) {
          }
        }
        fromSquare.Pieces = [movingPiece]; // Only Virus remains
      } else {
        // Virus is not stacked (shouldn't happen, but handle gracefully)
      }

      // CRITICAL: End the bonus turn by clearing the flag
      // This ensures the turn switches to the opponent after this move
      newGameState.skipTurnChange = false;
      newGameState.activeBonusTurnPieceId = null;
    } else if (moveInfo.move.moveType === MoveType.lane) {
      // ====== LANE SWAP ======
      // BUG FIX: Preserve other pieces on both squares (e.g., if Virus is infecting)
      const targetPiece = toSquare.Pieces[0];
      if (targetPiece) {
        // Get all pieces except the ones being swapped
        const fromPiecesWithoutMoving = fromSquare.Pieces.filter(p => p !== movingPiece);
        const toPiecesWithoutTarget = toSquare.Pieces.filter(p => p !== targetPiece);

        // Swap the primary pieces, keeping any "passengers" (housing/infecting pieces)
        fromSquare.Pieces = [...fromPiecesWithoutMoving, targetPiece];
        toSquare.Pieces = [...toPiecesWithoutTarget, movingPiece];
      }
    } else if (moveInfo.move.moveType === MoveType.house) {
      // ====== HOUSE (climbing piece onto friendly Tree, etc.) ======
      // Must be checked BEFORE the capture catch-all below, otherwise the
      // `|| toPiece` falls through and the friendly target (e.g. own Tree king)
      // gets wiped from the simulated board.
      const targetPiece = toSquare.Pieces[0];
      if (targetPiece && targetPiece.isLight === movingPiece.isLight) {
        const otherPiecesOnTarget = toSquare.Pieces.filter(p => p !== targetPiece);
        toSquare.Pieces = [...otherPiecesOnTarget, targetPiece, movingPiece];
        fromSquare.Pieces = fromSquare.Pieces.filter(p => p !== movingPiece);
        movingPiece.isHousing = true;
        targetPiece.isHousing = true;
      }
    } else if (moveInfo.move.moveType === MoveType.capture || moveInfo.move.toPiece) {
      // ====== CAPTURE ======
      const capturedPiece = toSquare.Pieces[0];

      // CRITICAL FIX: Handle Snake respawn ability
      // When a Snake is captured, it respawns on its home rank (row 0 for Light, row 7 for Dark)
      // at the SAME COLUMN where it was captured
      if (capturedPiece && capturedPiece.type === PieceType.Snake && !capturedPiece.hasShed) {
        const respawnRow = capturedPiece.isLight ? 0 : 7;
        const respawnCol = moveInfo.move.position.col; // Snake respawns at capture column

        // Check if respawn square allows respawn (matches MovementManager.ts logic)
        // Snake can respawn if square is EMPTY or has ONLY a Tree (can house with Tree)
        const respawnSquare = newGameState.board[respawnRow][respawnCol];
        const piecesAtRespawn = respawnSquare.Pieces;

        const canRespawn = piecesAtRespawn.length === 0 ||
                          (piecesAtRespawn.length === 1 &&
                           piecesAtRespawn[0].type === PieceType.Tree);

        if (canRespawn) {
          // Create a NEW Snake using Piece constructor (ensures proper initialization)
          const respawnedSnake = new Piece(
            PieceType.Snake,
            capturedPiece.isLight,
            null // direction
          );

          // Preserve king status if the captured Snake was a king
          if (capturedPiece.isKing) {
            respawnedSnake.isKing = true;
          }

          // Mark shed as used — snake only gets one bonus life
          respawnedSnake.hasShed = true;

          // Add respawned Snake to the board
          respawnSquare.Pieces.push(respawnedSnake);
        }
      }

      // Complete the capture
      toSquare.Pieces = [movingPiece];
      fromSquare.Pieces = fromSquare.Pieces.filter(p => p !== movingPiece);
    } else if (moveInfo.move.moveType === MoveType.trample) {
      // ====== TRAMPLE (Rhino charge) ======
      // Rhino captures ALL pieces along the path from fromPosition to toPosition
      const rowDiff = moveInfo.move.position.row - moveInfo.fromPosition.row;
      const colDiff = moveInfo.move.position.col - moveInfo.fromPosition.col;
      const rowStep = rowDiff === 0 ? 0 : rowDiff > 0 ? 1 : -1;
      const colStep = colDiff === 0 ? 0 : colDiff > 0 ? 1 : -1;

      let currentRow = moveInfo.fromPosition.row + rowStep;
      let currentCol = moveInfo.fromPosition.col + colStep;

      let trampledFrog = false; // Track if any Frog was trampled (for poison)

      // Traverse the path, capturing all pieces (excluding start, up to but not including end)
      while (currentRow !== moveInfo.move.position.row || currentCol !== moveInfo.move.position.col) {
        const currentSquare = newGameState.board[currentRow][currentCol];
        const trampledPieces = currentSquare.Pieces.slice(); // Copy array

        if (trampledPieces.length > 0) {
          // Check for Frogs (poison trigger - ANY color Frog poisons Rhino)
          if (trampledPieces.some(p => p.type === PieceType.Frog)) {
            trampledFrog = true;
          }

          // Handle Snake respawns for trampled Snakes
          trampledPieces.forEach(trampledPiece => {
            if (trampledPiece.type === PieceType.Snake && !trampledPiece.hasShed) {
              const respawnRow = trampledPiece.isLight ? 0 : 7;
              const respawnCol = currentCol; // Same column as capture

              const respawnSquare = newGameState.board[respawnRow][respawnCol];
              const piecesAtRespawn = respawnSquare.Pieces;

              const canRespawn = piecesAtRespawn.length === 0 ||
                                (piecesAtRespawn.length === 1 &&
                                 piecesAtRespawn[0].type === PieceType.Tree);

              if (canRespawn) {
                const respawnedSnake = new Piece(PieceType.Snake, trampledPiece.isLight, null);
                if (trampledPiece.isKing) respawnedSnake.isKing = true;
                respawnedSnake.hasShed = true;
                respawnSquare.Pieces.push(respawnedSnake);
              }
            }
          });

          // Remove all trampled pieces from this square
          currentSquare.Pieces = [];
        }

        currentRow += rowStep;
        currentCol += colStep;
      }

      // Handle final destination square (might have a piece to capture)
      const finalSquare = newGameState.board[moveInfo.move.position.row][moveInfo.move.position.col];
      const finalPieces = finalSquare.Pieces.slice();

      if (finalPieces.length > 0) {
        // Check for Frog at destination
        if (finalPieces.some(p => p.type === PieceType.Frog)) {
          trampledFrog = true;
        }

        // Handle Snake respawn at destination
        finalPieces.forEach(capturedPiece => {
          if (capturedPiece.type === PieceType.Snake && !capturedPiece.hasShed) {
            const respawnRow = capturedPiece.isLight ? 0 : 7;
            const respawnCol = moveInfo.move.position.col;

            const respawnSquare = newGameState.board[respawnRow][respawnCol];
            const piecesAtRespawn = respawnSquare.Pieces;

            const canRespawn = piecesAtRespawn.length === 0 ||
                              (piecesAtRespawn.length === 1 &&
                               piecesAtRespawn[0].type === PieceType.Tree);

            if (canRespawn) {
              const respawnedSnake = new Piece(PieceType.Snake, capturedPiece.isLight, null);
              if (capturedPiece.isKing) respawnedSnake.isKing = true;
              respawnedSnake.hasShed = true;
              respawnSquare.Pieces.push(respawnedSnake);
            }
          }
        });
      }

      // Move Rhino to final position
      toSquare.Pieces = [movingPiece];
      fromSquare.Pieces = fromSquare.Pieces.filter(p => p !== movingPiece);

      // Apply poison if any Frog was trampled (either along path or at destination)
      if (trampledFrog) {
        movingPiece.poisonedStatus = {
          isPoisoned: true,
          removeOnTurn: undefined // Poison is permanent until healed
        };
      }

      // Set mega charge flag if distance > 2
      const isMegaCharge = Math.abs(rowDiff) > 2 || Math.abs(colDiff) > 2;
      if (isMegaCharge) {
        movingPiece.hasCharged = true;
      }
    } else {
      // ====== NORMAL MOVE (including Virus infection) ======
      // CRITICAL: Check if a Virus is leaving this square
      const virusLeavingSquare = fromSquare.Pieces.find(p =>
        p.type === PieceType.Virus && p === movingPiece
      );

      if (virusLeavingSquare) {
        // VIRUS CAPTURE-ON-EXIT: Virus captures any piece it leaves behind (even allies!)
        const leftBehindPieces = fromSquare.Pieces.filter(p => p !== movingPiece);
        if (leftBehindPieces.length > 0) {
          const exitingVirus = movingPiece;
          if (exitingVirus) {
            const friendlyCasualties = leftBehindPieces.filter(p => p.isLight === exitingVirus.isLight);
            if (friendlyCasualties.length > 0) {
            }
          }
          fromSquare.Pieces = []; // Remove all pieces (virus captures them)
        } else {
          fromSquare.Pieces = [];
        }

        // Virus moves to destination (may start new infection if occupied)
        toSquare.Pieces.push(movingPiece);
      } else {
        // Normal piece movement (or non-Virus infecting another piece)
        // BUG FIX: If moving onto occupied square (non-capture), it's infection
        // Only Virus should stack. Other pieces capturing should use MoveType.capture
        if (toSquare.Pieces.length > 0 && movingPiece.type === PieceType.Virus) {
          // Virus infection: Stack on top (will be resolved in future turns)
          toSquare.Pieces.push(movingPiece);
        } else if (toSquare.Pieces.length > 0) {
          // Non-Virus moving onto occupied square shouldn't happen (should be capture/house/lane)
          // But handle it: treat as capture
          toSquare.Pieces = [movingPiece];
        } else {
          // Normal move to empty square
          toSquare.Pieces.push(movingPiece);
        }
        fromSquare.Pieces = fromSquare.Pieces.filter(p => p !== movingPiece);
      }
    }

    // UPDATE PIECE ABILITY STATES
    // Frog: ANY move uses the lily (whether leap or regular move)
    if (movingPiece.type === PieceType.Frog) {
      movingPiece.hasLilyJumped = true;
    }

    if (movingPiece.type === PieceType.Rhino) {
      if (moveInfo.move.moveType === MoveType.lane) {
        movingPiece.hasCharged = true;
      }
    }

    // CRITICAL: Process side effects (bonus turns, etc.)
    if (moveInfo.move.sideEffects && moveInfo.move.sideEffects.length > 0) {
      for (const sideEffect of moveInfo.move.sideEffects) {
        // BonusTurn effect: Set skipTurnChange and activeBonusTurnPieceId
        if (sideEffect.effect.type === "Bonus Turn" && sideEffect.piecesToEffect.length === 1) {
          const bonusTurnPiece = sideEffect.piecesToEffect[0];
          newGameState.skipTurnChange = true;
          newGameState.activeBonusTurnPieceId = bonusTurnPiece.id;

          // Mirror SwingMoveHandler.setSwingReturnBlock so the AI's next-ply
          // move generation can prune the immediate reverse swing. Without this,
          // bonus-turn chaining lets the Monkey oscillate (e.g. C1<->C3) and burn
          // search depth on a no-op.
          if (moveInfo.move.moveType === MoveType.swing) {
            newGameState.lastSwingFromPosition = moveInfo.fromPosition;
            newGameState.lastSwingToPosition = moveInfo.move.position;
            newGameState.lastSwingMonkeyId = bonusTurnPiece.id;
          }
        }
        // Add other side effect types here as needed
      }
    }

    // CRITICAL: Check for game-over conditions after the move
    // 1. King capture (elimination victory)
    // 2. King reaching opponent's home row (race victory)
    let lightKingExists = false;
    let darkKingExists = false;
    let lightKingRow = -1;
    let darkKingRow = -1;

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = newGameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (piece.isKing) {
            if (piece.isLight) {
              lightKingExists = true;
              lightKingRow = row;
            } else {
              darkKingExists = true;
              darkKingRow = row;
            }
          }
        }
      }
    }

    // King captured = elimination victory
    if (!lightKingExists) {
      newGameState.state = State.PostGame;
      newGameState.lightWon = false;
    } else if (!darkKingExists) {
      newGameState.state = State.PostGame;
      newGameState.lightWon = true;
    }
    // King reached home row = race victory
    else if (lightKingRow === 7) {
      newGameState.state = State.PostGame;
      newGameState.lightWon = true;
    } else if (darkKingRow === 0) {
      newGameState.state = State.PostGame;
      newGameState.lightWon = false;
    }

    // Switch turn (unless bonus turn is active)
    if (newGameState.skipTurnChange) {
      // Clear skipTurnChange for next simulation (bonus turn only skips ONCE)
      newGameState.skipTurnChange = false;
    } else {
      newGameState.isLightTurn = !newGameState.isLightTurn;
      // Clear bonus turn state when turn actually changes
      newGameState.activeBonusTurnPieceId = null;
    }

    return newGameState;
  } catch (error) {
    return null;
  }
}

/**
 * Check if a game state is terminal (game over)
 */
export function isTerminal(gameState: GameState): boolean {
  return (
    gameState.state === "Post Game" ||
    gameState.state === "Draw" ||
    gameState.state === "Terminated"
  );
}

/**
 * Strip FEN metadata for position comparison.
 * Mirrors FENHistoryManager.stripFENMetadata() logic.
 */
function stripFENMetadata(fen: string): string {
  let clean = fen;
  if (clean.startsWith('d')) clean = clean.substring(1);
  const atIdx = clean.indexOf('@');
  if (atIdx !== -1) clean = clean.substring(0, atIdx);
  const tildeIdx = clean.indexOf('~');
  if (tildeIdx !== -1) clean = clean.substring(0, tildeIdx);
  const hashIdx = clean.indexOf('#');
  if (hashIdx !== -1) clean = clean.substring(0, hashIdx);
  return clean;
}

/**
 * Check if a move would trigger a 3-fold repetition draw.
 * Call at minimax root level BEFORE committing to a move.
 *
 * Compares the resulting position against the real game's fenHistory.
 * If the position already appeared 2+ times, this move would be the 3rd = draw.
 */
export function wouldCauseThreefoldDraw(
  fenHistory: string[],
  resultState: GameState
): boolean {
  const resultFEN = stripFENMetadata(ZodiacFEN.encodeWithoutKings(resultState));
  let count = 0;
  for (const fen of fenHistory) {
    if (stripFENMetadata(fen) === resultFEN) {
      count++;
      if (count >= 2) return true;
    }
  }
  return false;
}
